import { isHiddenSysEntity } from '@tunasong/models'
import type { TunaThunkDispatch } from '@tunasong/redux'
import type { Entity, Persisted } from '@tunasong/schemas'
import { prefetchSearch, useSearch } from './search.hook.js'

export interface UseRecentProps {
  /** If specified, get only decendants of parentId */
  parentId?: string
  /** Entities to exclude from the list, e.g., if they are already shown */
  exclude?: Persisted<Entity>[]
  /** The maximum number of entities to return */
  maxCount?: number
}

/** The search behind `useRecent`, shared with `prefetchRecent` so both use one cache entry (GH #2903) */
const recentSearch = ({ parentId, maxCount = Number.MAX_SAFE_INTEGER }: Omit<UseRecentProps, 'exclude'>) => ({
  queryType: 'recent' as const,
  /**
   * A parent SCOPE, not a search string. Sent as `query` it reached the wire as `q`,
   * which the local handler ignores for a recent search — so a bandspace's
   * recent-activity grid listed every recent entity in the kernel.
   */
  parentId,
  size: maxCount,
})

/** Start `useRecent`'s search before it mounts */
export const prefetchRecent = (dispatch: TunaThunkDispatch, props: Omit<UseRecentProps, 'exclude'>) =>
  prefetchSearch(dispatch, recentSearch(props))

export const useRecent = ({ parentId, exclude, maxCount }: UseRecentProps) => {
  const { results, searching: isLoading, isSuccess, isError, retry } = useSearch(recentSearch({ parentId, maxCount }))

  const searchResults = results
    .filter(e => !isHiddenSysEntity(e))
    .filter(e => !Boolean(e.trash))
    .filter(e => !exclude?.some(ex => ex.id === e.id))
    .slice(0, maxCount ?? Number.MAX_SAFE_INTEGER)

  return { searchResults, isLoading, isSuccess, isError, retry }
}
